import React from "react";
import { BsFileText } from "react-icons/bs";
import { FiEdit } from "react-icons/fi";
import { MdArrowForward, MdClose, MdDone } from "react-icons/md";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { Attachment } from "lib/build/attachments";
import { Origin } from "lib/build/events";
import {
	HlaTyping,
	StatusByNephrologist
} from "lib/build/forms/ktx/patientCondition";

import { AttachmentTable } from "src/common/components/AttachmentTable";
import { Popover } from "src/common/components/Popover";
import { StatusBadge } from "src/common/components/StatusBadge";
import {
	Timeline,
	TimelineItem,
	TimelineItemValue
} from "src/common/components/Timeline";
import { useFormatName } from "src/common/hooks/useFormatName";
import { useKnownUsers } from "src/features/auth";
import { useOrgModParams } from "src/features/orgmod";
import { PatientLink } from "src/features/patient";
import { longStatusMessages } from "src/modules/ktx/messages";
import { PatientConditionValue } from "src/modules/ktx/status-history/types";
import { sidToForms } from "src/sidToForms";

import "./StatusHistoryTimeline.css";

const messages = defineMessages({
	neakCheck: {
		id: "ktx.status_history.neak_check",
		defaultMessage: "NEAK jogosultság ellenőrzés megtörtént és rendben van."
	},
	assignedUser: {
		id: "ktx.status_history.assigned_user",
		defaultMessage: "Véleményező orvos"
	},
	description: {
		id: "ktx.status_history.description",
		defaultMessage: "Megjegyzés"
	},
	transplantable: {
		id: "ktx.status_history.transplantable",
		defaultMessage:
			"Páciens a nefrológus véleménye szerint Transzplantálható."
	},
	notTransplantable: {
		id: "ktx.status_history.not_transplantable",
		defaultMessage:
			"Páciens a nefrológus véleménye szerint Nem Transzplantálható."
	},
	firstSampleSent: {
		id: "ktx.status_history.first_sample_sent",
		defaultMessage: "HLA Tipizálás - első minta elküldve"
	},
	secondSampleSent: {
		id: "ktx.status_history.second_sample_sent",
		defaultMessage: "HLA Tipizálás - második minta elküldve"
	},
	missingForms: {
		id: "ktx.status_history.missing_forms",
		defaultMessage: "Hiányzó vizsgálatok"
	}
});

interface ItemValue {
	origin: Origin;
	attachments: Attachment[];
	data: PatientConditionValue;
}

interface StatusHistoryTimelineProps {
	items: TimelineItem<ItemValue>[];
}

export const StatusHistoryTimeline: React.FunctionComponent<
	StatusHistoryTimelineProps
> = ({ items }) => (
	<Timeline<ItemValue>
		items={items}
		renderActions={renderActions}
		renderContent={renderContent}
	/>
);

function renderActions(value: ItemValue): React.ReactNode {
	return <Actions {...value} />;
}

const Actions: React.FunctionComponent<ItemValue> = ({
	origin,
	attachments
}) => {
	const { mod } = useOrgModParams();
	return (
		<>
			{origin.module === mod && (
				<PatientLink to={`/form/${origin.kind}/${origin.id}`}>
					<FiEdit />
				</PatientLink>
			)}
			{attachments.length !== 0 && (
				<Popover
					target={<BsFileText />}
					content={<AttachmentTable attachments={attachments} />}
				/>
			)}
		</>
	);
};

function renderContent({ data }: ItemValue): React.ReactNode {
	return <ItemContent {...data} />;
}

const ItemContent: React.FunctionComponent<PatientConditionValue> = ({
	subject,
	status,
	description,
	neakCheck,
	assignedUser,
	missingForms,
	statusByNephrologist,
	hlaTyping
}) => {
	const { mod } = useOrgModParams();
	const { formatMessage } = useIntl();

	const users = useKnownUsers();
	const formatName = useFormatName();

	const modFormMap = React.useMemo(() => {
		const map = new Map<string, { id: string; label: string }>();
		for (const [{ id, titleMessageId }] of Object.values(sidToForms[mod])) {
			if (!map.has(id)) {
				map.set(id, {
					id,
					label: formatMessage({ id: titleMessageId })
				});
			}
		}
		return Object.fromEntries(map.entries());
	}, [mod, formatMessage]);

	const missingFormLinks = React.useMemo(() => {
		if (missingForms === undefined || missingForms.length === 0) {
			return [];
		}
		let links = missingForms.map(id => modFormMap[id]);
		links = links.filter(l => l !== undefined);
		return links;
	}, [modFormMap, missingForms]);

	const isFirstSampleSent = React.useMemo(
		() =>
			Array.isArray(hlaTyping) &&
			hlaTyping.includes(HlaTyping.FIRST_SAMPLE_SENT),
		[hlaTyping]
	);

	const isSecondSampleSent = React.useMemo(
		() =>
			Array.isArray(hlaTyping) &&
			hlaTyping.includes(HlaTyping.SECOND_SAMPLE_SENT),
		[hlaTyping]
	);

	return (
		<>
			{status && (
				<TimelineItemValue
					value={
						<StatusBadge
							value={status}
							label={formatMessage(longStatusMessages[status])}
						/>
					}
				/>
			)}
			<TimelineItemValue
				value={
					<>
						{assignedUser !== undefined &&
							assignedUser in users && (
								<div className="status-history-timeline__text">
									<FormattedMessage
										{...messages.assignedUser}
									/>
									: {formatName(users[assignedUser])}
								</div>
							)}
						<div className="status-history-timeline__text">
							<FormattedMessage {...messages.description} />:{" "}
							{description}
						</div>
						{subject === undefined ? (
							<>
								{statusByNephrologist ===
									StatusByNephrologist.TRANSPLANTABLE && (
									<div className="status-history-timeline__list-item">
										<div className="status-history-timeline__list-item__icon">
											<MdDone />
										</div>
										<div className="status-history-timeline__list-item__text">
											<FormattedMessage
												{...messages.transplantable}
											/>
										</div>
									</div>
								)}
								{statusByNephrologist ===
									StatusByNephrologist.NOT_TRANSPLANTABLE && (
									<div className="status-history-timeline__list-item">
										<div className="status-history-timeline__list-item__icon">
											<MdClose />
										</div>
										<div className="status-history-timeline__list-item__text">
											<FormattedMessage
												{...messages.notTransplantable}
											/>
										</div>
									</div>
								)}
								{isFirstSampleSent && (
									<div className="status-history-timeline__list-item">
										<div className="status-history-timeline__list-item__icon">
											<MdDone />
										</div>
										<div className="status-history-timeline__list-item__text">
											<FormattedMessage
												{...messages.firstSampleSent}
											/>
										</div>
									</div>
								)}
								{isSecondSampleSent && (
									<div className="status-history-timeline__list-item">
										<div className="status-history-timeline__list-item__icon">
											<MdDone />
										</div>
										<div className="status-history-timeline__list-item__text">
											<FormattedMessage
												{...messages.secondSampleSent}
											/>
										</div>
									</div>
								)}
							</>
						) : (
							neakCheck && (
								<div className="status-history-timeline__list-item">
									<div className="status-history-timeline__list-item__icon">
										<MdDone />
									</div>
									<div className="status-history-timeline__list-item__text">
										<FormattedMessage
											{...messages.neakCheck}
										/>
									</div>
								</div>
							)
						)}
					</>
				}
			/>
			{missingFormLinks.length !== 0 && (
				<TimelineItemValue
					value={
						<>
							<div className="status-history-timeline__text">
								<FormattedMessage {...messages.missingForms} />:
							</div>
							{missingFormLinks.map(form => (
								<PatientLink
									key={form.id}
									className="status-history-timeline__link"
									to={`/form/${form.id}`}
								>
									<div className="status-history-timeline__list-item__icon">
										<MdArrowForward />
									</div>
									<div className="status-history-timeline__list-item__text">
										{form.label}
									</div>
								</PatientLink>
							))}
						</>
					}
				/>
			)}
		</>
	);
};
